import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../mockData';
import { MaskedLine } from './Reveal';

const Hero = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] });
  const yShapes = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={sectionRef} id="home" className="relative min-h-screen flex items-center bg-white overflow-hidden pt-20">
      {/* Parallax Background Pattern */}
      <motion.div style={{ y: yShapes }} className="absolute inset-0 z-0 opacity-5">
        <div className="absolute top-20 right-10 w-96 h-96 rounded-full border-2 border-brand-red animate-pulse-slow"></div>
        <div className="absolute bottom-20 left-10 w-64 h-64 rounded-full border-2 border-charcoal animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/3 w-40 h-40 rounded-full border-2 border-brand-red animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </motion.div>

      {/* Content */}
      <motion.div style={{ y: yContent }} className="container mx-auto px-6 py-20 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center space-x-2 bg-red-50 rounded-full px-6 py-2 mb-8"
          >
            <div className="w-2 h-2 bg-brand-red rounded-full animate-pulse"></div>
            <span className="text-brand-red text-sm font-semibold">
              Established {companyInfo.founded} | South Africa
            </span>
          </motion.div>

          {/* Kinetic Main Heading — masked line-by-line reveal */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-charcoal mb-8 leading-tight" data-testid="hero-heading">
            <MaskedLine delay={0.15}>Driving Value,</MaskedLine>
            <MaskedLine delay={0.35} className="mt-2">
              <span className="text-brand-red">Adding Solutions</span>
            </MaskedLine>
          </h1>

          {/* Supporting Text */}
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed"
          >
            {companyInfo.description}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button
              onClick={() => navigate('/services')}
              data-testid="hero-our-services-button"
              className="bg-brand-red hover:bg-red-700 text-white font-semibold text-lg px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all group"
              style={{ backgroundColor: '#E53935', color: '#FFFFFF' }}
            >
              Our Services
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              variant="outline"
              data-testid="hero-contact-us-button"
              className="border-2 border-charcoal text-charcoal hover:bg-charcoal hover:text-white font-semibold text-lg px-8 py-6 rounded-lg transition-all group"
            >
              Contact Us
              <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-16 flex flex-wrap justify-center gap-8 md:gap-12"
          >
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-brand-red mb-1">50+</div>
              <div className="text-sm text-gray-600">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-brand-red mb-1">{companyInfo.yearsExperience}+</div>
              <div className="text-sm text-gray-600">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-brand-red mb-1">100%</div>
              <div className="text-sm text-gray-600">Client Satisfaction</div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 to-transparent z-10"></div>
    </section>
  );
};

export default Hero;
